import { useEffect, useState } from "react"

import {
  ASYNC_TASK_POLL_INTERVAL_MS,
  useQueryAsyncTask,
} from "api/reactQuery/queries/tasks"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"

import {
  clearPersistedDeleteTransactionsTask,
  DeleteTransactionsView,
  getPersistedDeleteTransactionsTask,
  IPersistedDeleteTransactionsTask,
  persistDeleteTransactionsTask,
} from "./deleteTransactionsTaskStorage"

export const DELETE_TRANSACTIONS_TASK_TTL_MS = 30 * 60 * 1000

// Every query that must reload once a delete has finished, per view.
const queriesToReset: Record<DeleteTransactionsView, string[]> = {
  teacher: ["teacherTransactions", "teacherTransactionsIds"],
  schoolAdmin: [
    "teacherAdminSchoolTransactionsInfinite",
    "teacherAdminSchoolTransactionsIds",
  ],
}

interface IUseDeleteTransactionsTaskArgs {
  view: DeleteTransactionsView
  scopeId: number | undefined
}

const isStale = (task: IPersistedDeleteTransactionsTask) =>
  Date.now() - task.timestamp > DELETE_TRANSACTIONS_TASK_TTL_MS

export const useDeleteTransactionsTask = ({
  view,
  scopeId,
}: IUseDeleteTransactionsTaskArgs) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const queryClient = useQueryClient()
  const [task, setTask] = useState<IPersistedDeleteTransactionsTask | null>(
    () => {
      const persisted =
        scopeId === undefined
          ? null
          : getPersistedDeleteTransactionsTask(view, scopeId)
      return persisted && !isStale(persisted) ? persisted : null
    }
  )

  const resetLists = () => {
    queriesToReset[view].forEach((key) => queryClient.resetQueries([key]))
  }

  const finish = (
    ended: IPersistedDeleteTransactionsTask,
    toast: { title: string; variant: "success" | "error" | "info" }
  ) => {
    clearPersistedDeleteTransactionsTask(ended.view, ended.scopeId)
    setTask(null)
    showSnackbar(toast)
    resetLists()
  }

  const finishUnknown = (ended: IPersistedDeleteTransactionsTask) =>
    finish(ended, {
      title: t("allTransactions.deleteTaskStatusUnknown"),
      variant: "info",
    })

  useEffect(() => {
    const persisted =
      scopeId === undefined
        ? null
        : getPersistedDeleteTransactionsTask(view, scopeId)

    if (persisted && isStale(persisted)) {
      finishUnknown(persisted)
      return
    }
    setTask(persisted)
  }, [view, scopeId])

  useQueryAsyncTask({
    task_id: task?.taskId ?? null,
    options: {
      enabled: !!task,
      refetchInterval: ASYNC_TASK_POLL_INTERVAL_MS,
      refetchIntervalInBackground: true,
      retry: false,
      onSuccess: (response) => {
        if (!task) return

        const { status } = response.data
        if (status === "SUCCESS") {
          finish(task, {
            title: t("allTransactions.transactionsDeleted", {
              count: task.count,
            }),
            variant: "success",
          })
        } else if (status === "FAILURE") {
          finish(task, {
            title: t("allTransactions.deleteTaskFailed"),
            variant: "error",
          })
        } else if (isStale(task)) {
          finishUnknown(task)
        }
      },
      onError: (err) => {
        if (!task) return

        if (err.response?.status === 404 || isStale(task)) {
          finishUnknown(task)
        }
      },
    },
  })

  const startTask = (taskId: string, count: number) => {
    if (scopeId === undefined) return

    const started = { view, scopeId, taskId, count, timestamp: Date.now() }
    persistDeleteTransactionsTask(started)
    setTask(started)
  }

  return {
    activeTask: task ? { count: task.count } : null,
    startTask,
    resetLists,
  }
}
